import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const HomePage = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      setPlansError(false);
      const response = await api.get('/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setPlansError(true);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    if (!isAuthenticated) {
      navigate('/register', { state: { selectedPlan: plan } });
    } else {
      navigate('/dashboard', { state: { selectedPlan: plan } });
    }
  };

  return (
    <div>
      <nav className="navbar">
        <Link to="/" className="navbar-brand">
          🎙️ Steinmetz Mumble
        </Link>
        <div className="navbar-menu">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="btn btn-primary">
                Dashboard
              </Link>
              {isAdmin && (
                <Link to="/admin" className="btn btn-secondary">
                  Admin
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
              <Link to="/register" className="btn btn-primary">
                Sign Up
              </Link>
            </>
          )}
        </div>
      </nav>

      <div className="hero">
        <h1>Professional Push-to-Talk Server</h1>
        <p>Crystal clear, low-latency voice communication for teams, communities, and organizations. Hosted in Pittsburgh, PA with enterprise-grade reliability.</p>
      </div>

      <div className="container">
        <h2 style={{ textAlign: 'center', color: 'white', marginBottom: '2rem' }}>
          Choose Your Plan
        </h2>
        <div className="plans-grid">
          {plansLoading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.7)' }}>
              Loading plans...
            </div>
          ) : plansError ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1rem' }}>Unable to load plans right now.</p>
              <button className="btn btn-secondary" onClick={fetchPlans}>Try Again</button>
            </div>
          ) : plans.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.7)' }}>
              <p>Plans coming soon. <Link to="/register" style={{ color: 'white', fontWeight: '600' }}>Sign up</Link> to get notified.</p>
            </div>
          ) : (
            plans.map((plan, index) => (
              <div
                key={plan.id}
                className={`plan-card ${index === 1 ? 'featured' : ''}`}
              >
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  ${plan.price}
                  <span>/month</span>
                </div>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  {plan.description}
                </p>
                <ul className="plan-features">
                  <li>Up to {plan.max_users} users</li>
                  {plan.features?.quality && (
                    <li>{plan.features.quality.charAt(0).toUpperCase() + plan.features.quality.slice(1)} quality audio</li>
                  )}
                  {plan.features?.support && (
                    <li>{plan.features.support.charAt(0).toUpperCase() + plan.features.support.slice(1)} support</li>
                  )}
                  {plan.features?.channels && (
                    <li>{plan.features.channels} channels</li>
                  )}
                  {plan.features?.recording && <li>Voice recording</li>}
                  {plan.features?.custom_branding && <li>Custom branding</li>}
                </ul>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => handleSelectPlan(plan)}
                >
                  Get Started
                </button>
              </div>
            ))
          )}
        </div>

        {/* What is Mumble */}
        <div className="card" style={{ marginTop: '4rem' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '1rem', color: '#667eea' }}>
            What is Mumble?
          </h2>
          <p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 1.5rem', lineHeight: '1.7', color: '#555' }}>
            Mumble is an open-source, low-latency voice chat application designed for use during gaming, team collaboration, and community communication. Unlike consumer voice apps, Mumble uses a dedicated client-server architecture that delivers studio-quality audio with latency as low as 10ms — making it the gold standard for real-time voice communication.
          </p>
          <p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto', lineHeight: '1.7', color: '#555' }}>
            Steinmetz Mumble gives you your own managed server instance with zero setup headaches. We handle the infrastructure, security, and updates — you just connect and talk.
          </p>
        </div>

        {/* Why Choose Steinmetz Mumble */}
        <div className="card card-dark" style={{ marginTop: '2rem' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Why Choose Steinmetz Mumble?
          </h2>
          <div className="dashboard-grid">
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Encrypted Communication</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>All voice traffic is encrypted with TLS 1.3. Your conversations stay private — no eavesdropping, no data mining, no ads.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Ultra-Low Latency</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Mumble's OPUS codec and optimized networking deliver clear audio with as little as 10ms delay — far below Discord, Zoom, or Teams.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Always On</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Hosted on dedicated infrastructure with automatic failover. Your server is available 24/7 with no scheduled downtime windows.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Cross Platform</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Official clients for Windows, macOS, and Linux. Third-party apps available for iOS and Android. Connect from any device.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Push-to-Talk & Voice Activity</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Choose push-to-talk for controlled comms or voice activation for hands-free operation. Per-user volume controls and noise suppression built in.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Full Admin Control</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Create channels, set permissions, manage users, and configure access control lists — all from your web dashboard or the Mumble client.</p>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="card" style={{ marginTop: '2rem' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#667eea' }}>
            Who Uses Mumble?
          </h2>
          <div className="dashboard-grid">
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Gaming Communities</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>Raid groups, guilds, clans, and competitive teams rely on Mumble for its unmatched latency. When milliseconds matter, Mumble delivers.</p>
            </div>
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Remote Teams & Small Business</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>An always-on voice channel for your team. Drop in and out naturally — like working in the same office. No meeting links, no scheduling.</p>
            </div>
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Event Coordination</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>Tournaments, conventions, and live events use Mumble for backstage comms. Separate channels for organizers, volunteers, and participants.</p>
            </div>
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Content Creators & Podcasters</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>Record high-quality multi-party audio for podcasts and live streams. Mumble's OPUS codec provides broadcast-ready quality at low bandwidth.</p>
            </div>
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Education & Training</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>Study groups, tutoring sessions, and training cohorts benefit from Mumble's lightweight client and reliable connections on any bandwidth.</p>
            </div>
            <div style={{ padding: '1.5rem', background: '#f8f9ff', borderRadius: '12px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Privacy-Conscious Users</h3>
              <p style={{ lineHeight: '1.6', color: '#555' }}>No telemetry, no data harvesting, no account required on third-party platforms. Your server, your data, your rules.</p>
            </div>
          </div>
        </div>

        {/* Technical Specs */}
        <div className="card card-dark" style={{ marginTop: '2rem' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Technical Details
          </h2>
          <div className="dashboard-grid">
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>Audio Codec</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>OPUS codec at up to 96 kbps. Automatic bitrate adjustment based on network conditions. Supports sample rates up to 48 kHz.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>Network Protocol</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>TCP for control, UDP for voice. Automatic TCP fallback when UDP is blocked. Runs on port 64738 by default.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>Access Control</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Fine-grained ACLs per channel: speak, whisper, mute, move, and admin privileges. Certificate-based user authentication.</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', padding: '1.5rem', borderRadius: '12px' }}>
              <h3 style={{ marginBottom: '0.8rem' }}>Positional Audio</h3>
              <p style={{ lineHeight: '1.6', opacity: 0.9 }}>Supports 3D positional audio in compatible games. Hear teammates relative to their in-game position for immersive spatial awareness.</p>
            </div>
          </div>
        </div>

        {/* How to Connect */}
        <div className="card" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#667eea' }}>
            Getting Connected
          </h2>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <div className="connection-info">
              <div className="info-row">
                <span className="label">Server Address</span>
                <span className="value">mumble.steinmetz.ltd</span>
              </div>
              <div className="info-row">
                <span className="label">Port</span>
                <span className="value">64738</span>
              </div>
              <div className="info-row">
                <span className="label">Protocol</span>
                <span className="value">TCP/UDP</span>
              </div>
            </div>
            <div style={{ marginTop: '1.5rem', color: '#555', lineHeight: '1.8' }}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Quick Start:</p>
              <ol style={{ paddingLeft: '1.2rem' }}>
                <li>Download the Mumble client from <a href="https://www.mumble.info/downloads/" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', fontWeight: '500' }}>mumble.info</a> (Windows, macOS, Linux) or search your mobile app store for a Mumble-compatible client (Plumble for Android, Mumble for iOS)</li>
                <li>Open the client and go to Server &rarr; Connect &rarr; Add New</li>
                <li>Enter the server address and port shown above</li>
                <li>Choose a username and connect</li>
              </ol>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#888' }}>
                Need a managed channel with custom permissions? Sign up for a plan above and configure everything from your dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
